import { useFetch } from 'common.ui';
import { useState } from 'react';
import { userMigrationClient } from 'api/MinSideClients';
import { ApiUserMigrationStatusPutRequest, UserMigrationStatus, UserMigrationStatusResponse } from 'api/minside';
import { useMsal } from 'auth/MsalContext';

export type IUserMigrationStatus = [UserMigrationStatus | undefined, boolean, (status: UserMigrationStatus) => void];

export type UserMigrationStatusResult = UserMigrationStatusResponse | undefined;

const userMigrationSessionName = 'UserMigrationStatus';

export function useUserMigrationStatus(): IUserMigrationStatus {
  const { isAuthenticated } = useMsal();
  // Brukes bare til å trigge ny render etter at statusen er lagret i sesjonen
  const [, setSessionVersion] = useState(0);

  const fetchStatus = async (): Promise<UserMigrationStatusResult> => {
    if (!isAuthenticated) {
      return undefined;
    }
    const result = await userMigrationClient.apiUserMigrationStatusGet();
    if (!result) {
      return undefined;
    }
    return result;
  };

  const [fetchedStatus] = useFetch<UserMigrationStatusResult>(
    () => fetchStatus(),
    [undefined] as UserMigrationStatusResult,
    false,
    [isAuthenticated]
  );

  const getSessionStatus = (): UserMigrationStatus | null => {
    const value = window.sessionStorage.getItem(userMigrationSessionName);
    const status = value as UserMigrationStatus | null;
    return status;
  };

  const setSessionStatus = (status: UserMigrationStatus) => {
    window.sessionStorage.setItem(userMigrationSessionName, status);
  };

  const setStatus = async (status: UserMigrationStatus) => {
    const request = {
      userMigrationStatusUpdateRequest: {
        migrationStatus: status
      }
    } as ApiUserMigrationStatusPutRequest;
    await userMigrationClient.apiUserMigrationStatusPut(request);
    setSessionStatus(status);
    setSessionVersion((version) => version + 1);
  };

  // Sesjonen leses ved hver render, slik at andre instanser av hooken som har lagret
  // status også blir fanget opp når komponenten rendres på nytt (for eksempel ved sidebytte).
  const session = getSessionStatus();
  const migrationStatus: UserMigrationStatus | undefined =
    session || (fetchedStatus && fetchedStatus.migrationStatus) || undefined;

  const required =
    !!migrationStatus && [UserMigrationStatus.FirstAccess, UserMigrationStatus.Postponed].includes(migrationStatus);
  const migrationRequired = (required && !session) || false;

  return [migrationStatus, migrationRequired, setStatus];
}
